import { useApolloClient, useQuery, useReactiveVar } from '@apollo/client'
import { AccountCircleOutlined, Close as CloseIcon, ExpandMore, Launch } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, IconButton, TextField, Typography, useTheme } from '@mui/material'
import moment from 'moment'
import { ChangeEvent, FocusEvent, useEffect, useState } from 'react'
import { v4 } from 'uuid'
import { GetProfileDocument, Image, MediaFragmentFragmentDoc } from '../../../../../graphql/generated'
import { alertsVar } from '../../../../../providers/apollo/cache'
import { Alert as IAlert } from '../../../../../providers/apollo/models/alert'
import formatBytes from '../../../../../utils/formatBytes'
import useGetAlbums from '../hooks/useGetAlbums'
import useUpdateImage from '../hooks/useUpdateImage'
import TagManagement from './TagManagement'

interface Fields {
  title?: string | null
  description?: string | null
}

interface ImageDetailProps {
  id: string
  onClose: () => void
}

export default function ImageDetails({ id, onClose }: ImageDetailProps) {
  const theme = useTheme()
  const client = useApolloClient()
  const { error, data: currentUser } = useQuery(GetProfileDocument)
  const alerts = useReactiveVar(alertsVar)
  const media = client.readFragment<Image>({ id: `Image:${id}`, fragment: MediaFragmentFragmentDoc })
  const albums = useGetAlbums()
  const { mutation } = useUpdateImage()
  const uploadedDate = moment(media?.uploadDate)

  const [moreExpanded, setMoreExpanded] = useState(true)
  const [fields, setFields] = useState<Fields>({})

  useEffect(() => {
    setFields({
      title: media?.title || '',
      description: media?.description || ''
    })
  }, [media])

  const handleSubmit = (event: FocusEvent<HTMLInputElement>) => {
    event.preventDefault()
    if (!media) {
      const updatedAlerts: IAlert[] = [...alerts, { id: v4(), message: 'you are trying to delete an image that does not exist', severity: 'error' }]
      alertsVar(updatedAlerts)
      return
    }

    const variables = {
      imageId: media.id,
      description: fields.description,
      userTags: media.userTags,
      title: fields.title
    }

    mutation({ variables })
  }

  const handleSaveTags = (userTags: string[]) => {
    const img = media
    if (!img) {
      const updatedAlerts: IAlert[] = [...alerts, { id: v4(), message: 'you are trying to delete an image that does not exist', severity: 'error' }]
      alertsVar(updatedAlerts)
      return
    }

    const variables = {
      imageId: id,
      title: img.title,
      description: img.description,
      userTags
    }

    mutation({ variables })
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedFields = { ...fields }
    const field = event.target.name as keyof Fields
    updatedFields[field] = event.target.value
    setFields(updatedFields)
  }

  return (
    <>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ padding: 2 }}>
        <Typography sx={{ fontSize: 18, color: '#081230' }} data-test="image-details-drawer">
          Details
        </Typography>
        <IconButton onClick={onClose} data-test="close" size="large">
          <CloseIcon />
        </IconButton>
      </Grid>
      {!error && (
        <Grid
          container
          direction="row"
          alignItems="center"
          sx={{ borderTop: '1px solid #EAEAEA', borderBottom: '1px solid #EAEAEA', color: '#9195A4', padding: theme.spacing(2) }}
        >
          <AccountCircleOutlined fontSize="small" />
          <span style={{ marginLeft: '10px' }} data-test="username">
            {currentUser?.getProfile?.username}
          </span>
        </Grid>
      )}
      <div style={{ padding: 16 }}>
        <Typography sx={{ color: '#9195A4' }}>Album</Typography>
        {albums.allAlbums &&
          albums.allAlbums
            .filter((a) => a.id === media?.albumId)
            .map((a) => (
              <Button
                href={`/u/${currentUser?.getProfile?.username}/a/${a.id}`}
                key={a.id}
                sx={{
                  marginTop: theme.spacing(1),
                  marginBottom: theme.spacing(2),
                  padding: theme.spacing(1, 0),
                  textTransform: 'unset',
                  '& svg': {
                    marginRight: theme.spacing(1.5)
                  }
                }}
                color="primary"
                variant="text"
              >
                <Launch />
                {a.title}
              </Button>
            ))}
        <Box mb={2}>
          <TextField
            data-test="title"
            label="Title"
            placeholder="Title"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="title"
            onBlur={handleSubmit}
            value={fields.title || ''}
          />
        </Box>
        <Box mb={2}>
          <TextField
            data-test="description"
            label="Description"
            placeholder="Description"
            variant="outlined"
            multiline
            rows={4}
            fullWidth
            onChange={handleChange}
            name="description"
            onBlur={handleSubmit}
            value={fields.description || ''}
          />
        </Box>
      </div>
      <div>
        <Accordion
          expanded={moreExpanded}
          onChange={(_e, expanded) => setMoreExpanded(expanded)}
          sx={{
            boxShadow: 'none',
            '&:before': {
              display: 'none'
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ background: moreExpanded ? 'rgba(6, 114, 203, 0.08)' : '', color: moreExpanded ? '#0672CB' : '' }}
          >
            <Typography>More Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <div style={{ padding: 16 }}>
              <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                  Upload Date:
                </Typography>
                <div>
                  <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                    {uploadedDate.format('MMM D[,] YYYY [at] LT')}
                  </Typography>
                </div>
              </Grid>
              {media?.dateTaken && (
                <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                  <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                    Date Taken:
                  </Typography>
                  <div>
                    <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                      {moment(media?.dateTaken).format('MMM D[,] YYYY [at] LT')}
                    </Typography>
                  </div>
                </Grid>
              )}
              <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                  Original Filename:
                </Typography>
                <div>
                  <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                    {media?.originalFilename}
                  </Typography>
                </div>
              </Grid>
              <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                  File Size:
                </Typography>
                <div>
                  <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                    {formatBytes(media?.originalImage?.size)}
                  </Typography>
                </div>
              </Grid>
              {media?.originalImage?.width && media?.originalImage?.height && (
                <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                  <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                    Image Size:
                  </Typography>
                  <div>
                    <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                      {media?.originalImage?.width}px x {media?.originalImage?.height}px
                    </Typography>
                  </div>
                </Grid>
              )}
              {media?.originalImage?.exif?.cameraBrand && (
                <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                  <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                    Camera Brand:
                  </Typography>
                  <div>
                    <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                      {media?.originalImage?.exif?.cameraBrand}
                    </Typography>
                  </div>
                </Grid>
              )}
              {media?.originalImage?.exif?.cameraModel && (
                <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                  <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                    Camera Model:
                  </Typography>
                  <div>
                    <Typography sx={{ color: '#2F364E', paddingLeft: '5px', fontStyle: 'italic', textTransform: 'none' }} variant="overline">
                      {media?.originalImage?.exif?.cameraModel}
                    </Typography>
                  </div>
                </Grid>
              )}
              {!media?.isMobileUpload && (
                <Grid container sx={{ padding: theme.spacing(0.25, 0) }}>
                  <Typography sx={{ paddingRight: '5px', fontWeight: 800 }} variant="overline">
                    Mobile Upload
                  </Typography>
                </Grid>
              )}
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
      <TagManagement handleSaveTags={handleSaveTags} currentImage={media} />
    </>
  )
}
